import React from 'react';
import { Check } from 'lucide-react';
import { STEPS, stepIndex, firstIncompleteStepPath } from './steps';

/**
 * Vertical wizard stepper for the brand rail. One node per visible step; state
 * is computed from the current step index, not CSS. Done steps show a check
 * (colour is never the only signal); the active node is brand-filled with a glow
 * ring and marked aria-current. On mobile the rail collapses to a slim
 * horizontal bar of nodes (handled in onboarding.css).
 *
 * The wizard is three steps, then a "Preparing your dashboard" tail. The tail
 * is dim while the merchant is still working through the steps and animates
 * once they are all behind them, so the rail ends on a calm in-progress state
 * rather than a fourth numbered circle.
 *
 * @param {{ currentKey: string, state?: object }} props
 */
const CAPTIONS = {
  business: 'Account details',
  'use-cases': 'Tell us your goals',
};

const Stepper = ({ currentKey, state }) => {
  const current = stepIndex(currentKey);
  // Steps before the first incomplete step are done; when onboarding is complete,
  // firstIncompleteStepPath is null and every step counts as done.
  const incompletePath = firstIncompleteStepPath(state);
  const doneThrough = incompletePath
    ? STEPS.findIndex((s) => s.path === incompletePath)
    : STEPS.length;

  // The tail takes over once every step is behind them, or when the route is
  // not one of the three (Verify / Test payment, kept but no longer steps).
  const isPreparing = doneThrough >= STEPS.length || current < 0;
  const tailSegDone = doneThrough >= STEPS.length;

  return (
    <ol className="ob-stepper" aria-label="Onboarding progress">
      {STEPS.map((step, i) => {
        const isDone = i < doneThrough && i !== current;
        const isCurrent = i === current;
        const status = isCurrent ? 'current' : isDone ? 'done' : 'todo';
        // The connector above node i is completed when the previous step is done.
        const segDone = i > 0 && i <= doneThrough;
        return (
          <li
            key={step.key}
            className={`ob-step ${status}${segDone ? ' seg-done' : ''}`}
            aria-current={isCurrent ? 'step' : undefined}
          >
            <span className="ob-step-node" aria-hidden="true">
              {isDone ? <Check size={14} strokeWidth={3} /> : i + 1}
            </span>
            <span className="ob-step-text">
              <span className="ob-step-label">{step.label}</span>
              {CAPTIONS[step.key] && (
                <span className="ob-step-caption">{CAPTIONS[step.key]}</span>
              )}
            </span>
            {isDone && <span className="ob-visually-hidden"> (completed)</span>}
          </li>
        );
      })}

      <li
        className={`ob-step ob-tail${isPreparing ? ' is-active' : ''}${tailSegDone ? ' seg-done' : ''}`}
        /* Announced once when it becomes the live state, not polled. */
        role={isPreparing ? 'status' : undefined}
      >
        <span className="ob-tail-node" aria-hidden="true">
          <span className="ob-tail-arc" />
          <span className="ob-tail-dot" />
        </span>
        <span className="ob-step-text ob-tail-text">
          <span className="ob-tail-label">Preparing your dashboard</span>
          <span className="ob-tail-bar" aria-hidden="true"><i /></span>
        </span>
      </li>
    </ol>
  );
};

export default Stepper;
